import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, OnDestroy, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

import { ButtonComponent } from '../../../../shared/components/ui/button/button.component';
import { ToastService } from '../../../../shared/services/toast.service';
import { ChatTeamAdminApiService } from '../../services/chat-team-admin-api.service';
import { ChatTeamAdmin } from '../../models/chat-team-admin.model';

@Component({
  selector: 'app-chat-teams-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent],
  template: `
    <div class="p-6 space-y-4">
      @if (error) {
        <div class="p-3 bg-error-50 dark:bg-error-500/10 border border-error-200 dark:border-error-500/20 rounded-lg text-sm text-error-700 dark:text-error-400">{{ error }}</div>
      }
      <form [formGroup]="form" (ngSubmit)="guardar()" class="space-y-4">
        <div>
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Nombre <span class="text-error-500">*</span>
          </label>
          <input type="text" formControlName="name" maxlength="120" placeholder="Ej: Soporte nivel 1"
            class="w-full px-2.5 py-1.5 text-sm border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500"
            [class.border-error-500]="hasError('name')"
            [class.border-gray-300]="!hasError('name')" />
          @if (hasError('name')) {
            <p class="mt-1 text-xs text-error-600 dark:text-error-400">El nombre es obligatorio.</p>
          }
        </div>

        <div>
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Descripción</label>
          <textarea formControlName="description" rows="3" maxlength="500" placeholder="Para qué sirve este equipo"
            class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"></textarea>
        </div>

        <div>
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Idioma principal</label>
          <select formControlName="defaultLanguage"
            class="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500">
            @for (idioma of idiomas; track idioma.code) {
              <option [value]="idioma.code">{{ idioma.label }}</option>
            }
          </select>
          <p class="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
            Idioma en el que atiende el equipo por defecto. Cada miembro puede cubrir otros.
          </p>
        </div>

        <label class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input type="checkbox" formControlName="active" class="rounded border-gray-300 dark:border-gray-600" />
          Equipo activo
        </label>
        <p class="text-[11px] text-gray-500 dark:text-gray-400 -mt-2">
          Un equipo inactivo no recibe conversaciones nuevas y no aparece en las reglas de enrutamiento.
        </p>

        <div class="flex justify-end gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
          <app-button variant="outline" type="button" (btnClick)="cancelar()">Cancelar</app-button>
          <app-button type="button" [disabled]="saving" (btnClick)="guardar()">
            @if (saving) { Guardando… } @else { {{ isEditing ? 'Actualizar' : 'Guardar' }} }
          </app-button>
        </div>
      </form>
    </div>
  `
})
export class ChatTeamsFormComponent implements OnDestroy {
  @Output() saved = new EventEmitter<ChatTeamAdmin>();
  @Output() cancelled = new EventEmitter<void>();

  private fb = inject(FormBuilder);
  private api = inject(ChatTeamAdminApiService);
  private toast = inject(ToastService);

  readonly idiomas = [
    { code: 'es', label: 'Español' },
    { code: 'en', label: 'English' },
    { code: 'pt', label: 'Português' },
    { code: 'fr', label: 'Français' },
    { code: 'de', label: 'Deutsch' },
    { code: 'it', label: 'Italiano' }
  ];

  isEditing = false;
  itemId: number | null = null;
  saving = false;
  error: string | null = null;

  form: FormGroup = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(120)]],
    description: [''],
    defaultLanguage: ['es'],
    active: [true]
  });

  private readonly destroy$ = new Subject<void>();

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  reset(): void {
    this.isEditing = false;
    this.itemId = null;
    this.error = null;
    this.saving = false;
    this.form.reset({ name: '', description: '', defaultLanguage: 'es', active: true });
  }

  loadTeam(id: number): void {
    this.isEditing = true;
    this.itemId = id;
    this.api.getTeamById(id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (item) => this.form.patchValue({
        name: item.name,
        description: item.description ?? '',
        defaultLanguage: item.defaultLanguage ?? 'es',
        active: item.active
      }),
      error: (err) => { this.error = err.error?.message || 'No se pudo cargar el equipo.'; }
    });
  }

  hasError(field: string): boolean {
    const ctrl = this.form.get(field);
    return !!(ctrl && ctrl.invalid && (ctrl.dirty || ctrl.touched));
  }

  guardar(): void {
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }
    this.saving = true;
    this.error = null;
    const raw = this.form.value;
    const request = {
      name: (raw.name ?? '').trim(),
      description: raw.description || null,
      defaultLanguage: raw.defaultLanguage || 'es',
      active: !!raw.active
    };
    const obs = this.isEditing && this.itemId != null
      ? this.api.updateTeam(this.itemId, request)
      : this.api.createTeam(request);

    obs.pipe(takeUntil(this.destroy$)).subscribe({
      next: (item) => {
        this.saving = false;
        if (this.isEditing) this.toast.updated('Equipo de chat', `Se actualizó "${item.name}" correctamente.`);
        else this.toast.created('Equipo de chat', `Se creó "${item.name}". Agrégale miembros para que reciba conversaciones.`);
        this.saved.emit(item);
      },
      error: (err) => {
        this.saving = false;
        const msg = err.error?.message || 'No se pudo guardar el equipo.';
        this.error = msg;
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  cancelar(): void { this.cancelled.emit(); }
}
